/**
 * Application.LeadSource packs two fields into one column: the lead-source type and the borrower's
 * free-text referral source, joined as `${type}-${source}`, or the type alone when nothing was typed.
 *
 * The separator is not escaped, so only the FIRST hyphen after the type is a separator - every later
 * one is the borrower's own ("Jean-Luc Martin", "Anne-Marie", a hyphenated site name). Splitting on
 * every hyphen kept only the first word, and the next save wrote the truncated text back for good.
 */
export class LeadSourceValue {
  static compose = (leadSourceType: string, leadSource: string): string =>
    leadSource ? `${leadSourceType}-${leadSource}` : leadSourceType;

  /**
   * A known type is matched as a whole prefix first (longest wins), so a type value that itself
   * contains a hyphen still parses; anything else falls back to the first hyphen, which is correct
   * for every type value in use today. The format cannot be read unambiguously in general: with two
   * known types where one is the other plus a hyphenated suffix ('Other' and 'Other-X'), 'Other-X-Y'
   * reads as the longer type. No catalogue value collides like that.
   */
  static parse = (stored: string, knownTypes: string[]): ParsedLeadSource => {
    if (!stored) {
      return { leadSourceType: null, leadSource: null };
    }

    const knownType = [...knownTypes]
      .filter((type) => !!type)
      .sort((a, b) => b.length - a.length)
      .find((type) => stored === type || stored.startsWith(`${type}-`));
    if (knownType) {
      return {
        leadSourceType: knownType,
        leadSource: stored.slice(knownType.length + 1) || null,
      };
    }

    const separator = stored.indexOf('-');
    if (separator < 0) {
      return { leadSourceType: stored, leadSource: null };
    }
    return {
      leadSourceType: stored.slice(0, separator),
      leadSource: stored.slice(separator + 1) || null,
    };
  };
}

export interface ParsedLeadSource {
  leadSourceType: string;
  leadSource: string;
}
